import { PLANS, type PlanCode } from "@byfluent/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Loading,
  Modal,
  PageHeader,
  Stat,
  TextInput,
} from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { brl, dateBR, monthLabel } from "../../lib/format";
import type { FinancePayment, PayoutSummary, Wallet } from "../../lib/types";
import { PayoutsCard, WalletCard } from "../sales/SellerFinance";

interface TeacherFinanceData {
  month: string;
  thisMonth: { revenueCents: number; active: number; overdue: number; suspended: number };
  forecastCents: number;
  history: { month: string; revenueCents: number; payments: FinancePayment[] }[];
  wallet: Wallet | null;
  payouts: PayoutSummary;
  notesToIssue: {
    paymentId: string;
    lessonsCents: number;
    contractorName: string;
    cpfMasked: string | null;
    date: string;
  }[];
}

export function TeacherFinance() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["teacher-finance"],
    queryFn: () => api.get<TeacherFinanceData>("/v1/teachers/me/finance"),
  });
  const [marking, setMarking] = useState<string | null>(null);
  const [note, setNote] = useState({ number: "", issuedAt: new Date().toISOString().slice(0, 10) });
  const mark = useMutation({
    mutationFn: () => api.post(`/v1/teachers/me/fiscal-notes/${marking}/issued`, note),
    onSuccess: () => {
      setMarking(null);
      setNote({ number: "", issuedAt: new Date().toISOString().slice(0, 10) });
      qc.invalidateQueries({ queryKey: ["teacher-finance"] });
    },
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert kind="risk">{errorMessage(q.error)}</Alert>;
  const d = q.data;
  return (
    <>
      <PageHeader
        title="Financeiro"
        subtitle={`Sua parte das aulas, repassada pela ByFluent à sua subconta no ${d.payouts.businessDay}º dia útil do mês seguinte ao pagamento. ${monthLabel(d.month)}.`}
      />
      <div className="grid cols-4 keep-2">
        <Stat label="Receita confirmada no mês" value={brl(d.thisMonth.revenueCents)} />
        <Stat label="Previsão (próximos 30 dias)" value={brl(d.forecastCents)} />
        <Stat label="Alunos ativos" value={d.thisMonth.active} />
        <Stat
          label="Em atraso / suspensos"
          value={`${d.thisMonth.overdue} / ${d.thisMonth.suspended}`}
        />
      </div>
      <div className="grid cols-2" style={{ marginTop: 16 }}>
        <div className="stack">
          <Card title="Notas a emitir">
            <p className="small muted" style={{ marginBottom: 12 }}>
              Pagamentos do mês anterior sem nota. A nota das aulas é sua, emitida na prefeitura ou
              na Asaas.
            </p>
            {d.notesToIssue.length === 0 ? <p className="muted">Nenhuma nota pendente.</p> : null}
            {d.notesToIssue.map((n) => (
              <div
                key={n.paymentId}
                className="row between"
                style={{ minHeight: 52, borderTop: "1px solid var(--line)" }}
              >
                <div className="small">
                  <strong>{n.contractorName}</strong> · CPF {n.cpfMasked}
                  <div className="muted">
                    {brl(n.lessonsCents)} · pago em {dateBR(n.date)}
                  </div>
                </div>
                <Button size="small" variant="secondary" onClick={() => setMarking(n.paymentId)}>
                  Marcar como emitida
                </Button>
              </div>
            ))}
          </Card>
          <Card title="Histórico">
            {d.history.length === 0 ? (
              <p className="muted">Ainda não há pagamentos confirmados.</p>
            ) : null}
            {d.history.map((m) => (
              <details key={m.month} open={m.month === d.month} style={{ marginBottom: 12 }}>
                <summary className="row between" style={{ cursor: "pointer", minHeight: 44 }}>
                  <strong>{monthLabel(m.month)}</strong>
                  <span>{brl(m.revenueCents)}</span>
                </summary>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Aluno</th>
                        <th>Plano</th>
                        <th className="num">Aulas</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.payments.map((p) => (
                        <tr key={p.paymentId}>
                          <td>{p.student}</td>
                          <td>{PLANS[p.planCode as PlanCode]?.name ?? p.planCode}</td>
                          <td className="num">{brl(p.lessonsCents)}</td>
                          <td>{dateBR(p.date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </Card>
        </div>
        <div className="stack">
          <PayoutsCard payouts={d.payouts} />
          <WalletCard wallet={d.wallet} />
        </div>
      </div>
      {marking ? (
        <Modal title="Nota emitida" onClose={() => setMarking(null)}>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              mark.mutate();
            }}
          >
            <TextInput
              label="Número da nota"
              required
              value={note.number}
              onChange={(e) => setNote({ ...note, number: e.target.value })}
            />
            <TextInput
              label="Data de emissão"
              type="date"
              required
              value={note.issuedAt}
              onChange={(e) => setNote({ ...note, issuedAt: e.target.value })}
            />
            {mark.error ? <Alert kind="risk">{errorMessage(mark.error)}</Alert> : null}
            <Button type="submit" loading={mark.isPending}>
              Salvar
            </Button>
          </form>
        </Modal>
      ) : null}
    </>
  );
}
